import {
	ChevronDownIcon,
	cn,
	type DeliveryMethod,
	deliveryMethodIcons,
} from "@au-schein-de/au-ui-library";
import { DELIVERY_METHOD_KINDS } from "@dransay/api/models";
import { MapPinIcon } from "lucide-react";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useOutsideDismiss } from "~/hooks/use-outside-dismiss.hook";
import { useSwipeDismiss } from "~/hooks/use-swipe-dismiss.hook";
import type { DeliveryMethodSelectorContentProps } from "./delivery-method-selector.shared";

interface DeliveryMethodSelectorMobilePanelProps
	extends DeliveryMethodSelectorContentProps {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
}

const PANEL_ANIMATION_DURATION_MS = 300;

export function DeliveryMethodSelectorMobilePanel({
	selectedMethod,
	onMethodSelect,
	deliveryMethodLabel,
	getMethodLabel,
	addressLabel,
	addressText,
	isAddressPlaceholder,
	addressActionLabel,
	onAddressAction,
	isOpen,
	onOpenChange,
	hiddenMethods,
	expressPromoSlot,
}: Readonly<DeliveryMethodSelectorMobilePanelProps>) {
	const panelId = useId();
	const deliveryMethodHeadingId = useId();
	const addressHeadingId = useId();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const onOpenChangeRef = useRef(onOpenChange);
	const [isPanelVisible, setIsPanelVisible] = useState(isOpen);
	const selectedDeliveryMethod = selectedMethod as DeliveryMethod;
	const visibleMethods = useMemo(
		() =>
			hiddenMethods?.length
				? DELIVERY_METHOD_KINDS.filter((m) => !hiddenMethods.includes(m))
				: DELIVERY_METHOD_KINDS,
		[hiddenMethods],
	);

	useEffect(() => {
		onOpenChangeRef.current = onOpenChange;
	}, [onOpenChange]);

	const closePanel = useCallback(() => {
		onOpenChangeRef.current(false);
		requestAnimationFrame(() => triggerRef.current?.focus());
	}, []);

	const handleAddressAction = useCallback(() => {
		if (!onAddressAction) {
			return;
		}
		closePanel();
		void onAddressAction();
	}, [closePanel, onAddressAction]);

	const togglePanel = useCallback(() => {
		onOpenChangeRef.current(!isOpen);
	}, [isOpen]);

	const { callbackRef: swipeCallbackRef } = useSwipeDismiss({
		onDismiss: closePanel,
		isEnabled: isOpen,
	});

	useOutsideDismiss({ isOpen, wrapperRef, onDismiss: closePanel });

	useEffect(() => {
		if (isOpen) {
			setIsPanelVisible(true);
			requestAnimationFrame(() => {
				const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
					"input, button, [tabindex]:not([tabindex='-1'])",
				);
				firstFocusable?.focus();
			});
			return;
		}

		const timeout = window.setTimeout(() => {
			setIsPanelVisible(false);
		}, PANEL_ANIMATION_DURATION_MS);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [isOpen]);

	return (
		<div ref={wrapperRef} className="relative h-10 w-full">
			{!isPanelVisible && (
				<button
					ref={triggerRef}
					type="button"
					data-testid="delivery-method-selector-mobile-row"
					className="flex h-10 w-full items-center justify-between cursor-pointer"
					aria-label={`${deliveryMethodLabel}: ${getMethodLabel(selectedMethod)}, ${addressLabel}: ${addressText}`}
					aria-haspopup="dialog"
					aria-expanded={isOpen}
					aria-controls={panelId}
					onClick={togglePanel}
				>
					<span
						data-testid="delivery-method-selector-mobile-trigger"
						className="flex items-center gap-2 py-3 pr-4 text-ansay-300"
					>
						<span
							className="size-4 [&_svg]:pointer-events-none"
							aria-hidden="true"
						>
							{deliveryMethodIcons[selectedDeliveryMethod]}
						</span>
						<span
							data-testid="delivery-method-selector-mobile-delivery-type"
							className="text-body-sm-emphasis"
						>
							{getMethodLabel(selectedMethod)}
						</span>
						<span
							aria-hidden="true"
							className="transition-transform [&_svg]:pointer-events-none"
						>
							<ChevronDownIcon size={5} />
						</span>
					</span>

					<span
						data-testid="delivery-method-selector-mobile-address"
						className={cn(
							"truncate",
							isAddressPlaceholder
								? "text-body-sm-emphasis text-ansay-300"
								: "text-body-sm-emphasis text-gray-100",
						)}
					>
						{addressText}
					</span>
				</button>
			)}

			{isPanelVisible && (
				<>
					{createPortal(
						<button
							type="button"
							data-testid="delivery-method-selector-mobile-backdrop"
							className={cn(
								"fixed top-[110px] left-0 right-0 bottom-0 z-[19] bg-[rgba(16,17,15,0.5)] transition-opacity duration-200",
								isOpen ? "opacity-100" : "pointer-events-none opacity-0",
							)}
							aria-label="Close delivery panel"
							onClick={closePanel}
						/>,
						document.body,
					)}
					<div
						ref={(node) => {
							panelRef.current = node;
							swipeCallbackRef(node);
						}}
						id={panelId}
						role="dialog"
						aria-labelledby={`${deliveryMethodHeadingId} ${addressHeadingId}`}
						data-testid="delivery-method-selector-mobile-panel"
						className={cn(
							"absolute -left-4 -right-4 top-0 z-50 flex flex-col gap-6 overflow-hidden rounded-b-[24px] bg-ansay-900 px-4 transition-[max-height,opacity,transform] duration-200 ease-out",
							isOpen
								? "max-h-[520px] translate-y-0 opacity-100 pt-4 pb-4"
								: "max-h-0 -translate-y-1 opacity-0 pt-0 pb-0 pointer-events-none",
						)}
					>
						<fieldset className="flex flex-col gap-2">
							<legend className="sr-only">{deliveryMethodLabel}</legend>
							<span
								id={deliveryMethodHeadingId}
								className="text-body-emphasis text-gray-100"
							>
								{deliveryMethodLabel}
							</span>
							<div className="flex gap-1 rounded-xl bg-white p-1">
								{visibleMethods.map((method) => (
									<label
										key={method}
										data-testid={`delivery-method-selector-mobile-option-label-${method}`}
										className={cn(
											"flex h-10 flex-1 cursor-pointer items-center justify-center gap-[5px] rounded-xl p-2 text-body-sm-emphasis transition-colors",
											method === selectedMethod
												? "bg-ansay-900 text-ansay-300"
												: "bg-gray-200 text-gray-700 hover:bg-gray-300",
										)}
									>
										<input
											type="radio"
											name="delivery-method-selector-mobile"
											className="sr-only"
											checked={method === selectedMethod}
											onChange={() => {
												void onMethodSelect(method, closePanel);
											}}
											data-testid={`delivery-method-selector-mobile-option-${method}`}
										/>
										<span
											aria-hidden="true"
											className="[&_svg]:pointer-events-none"
										>
											{deliveryMethodIcons[method as DeliveryMethod]}
										</span>
										<span>{getMethodLabel(method)}</span>
									</label>
								))}
							</div>
						</fieldset>

						{/* No wrapper div: a slot that renders null must add no box, or
						the flex gap above/below would still apply to an empty node. */}
						{expressPromoSlot}

						<div className="flex flex-col gap-2">
							<span
								id={addressHeadingId}
								className="text-body-emphasis text-gray-100"
							>
								{addressLabel}
							</span>
							<div className="flex h-12 items-center justify-between rounded-xl bg-gray-100 px-4">
								<button
									type="button"
									data-testid="delivery-method-selector-mobile-address-field"
									className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left disabled:cursor-default"
									onClick={handleAddressAction}
									disabled={!onAddressAction}
									aria-label={`${addressLabel}: ${addressText}`}
								>
									<MapPinIcon
										aria-hidden="true"
										className="size-6 shrink-0 text-gray-700"
									/>
									<span className="truncate text-body-sm tracking-[-0.28px] text-gray-700">
										{addressText}
									</span>
								</button>
								{addressActionLabel && (
									<button
										type="button"
										data-testid="delivery-method-selector-mobile-address-action"
										className="shrink-0 text-body-emphasis text-ansay-700 cursor-pointer"
										onClick={handleAddressAction}
										disabled={!onAddressAction}
									>
										{addressActionLabel}
									</button>
								)}
							</div>
						</div>

						<button
							type="button"
							data-testid="delivery-method-selector-mobile-grabber"
							className="flex w-full cursor-pointer justify-center py-2"
							aria-label="Close delivery panel"
							onClick={closePanel}
						>
							<span className="h-1 w-[164px] rounded-[40px] bg-white" />
						</button>
					</div>
				</>
			)}
		</div>
	);
}
